import { useEffect } from 'react'
import { motion } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, Bot } from 'lucide-react'
import { PavelChatBody } from '../PavelChatBody'

const F = 'Roboto, sans-serif'

interface Props {
  onClose: () => void
  /** Optional canned assistant intro for topic-specific entry (e.g. SOS button). */
  seedAssistantMessage?: string
  /** Optional quick-reply chips for the current topic. */
  suggestedReplies?: string[]
  /** Optional header subtitle override (e.g. "🆘 SOS-режим"). */
  topicLabel?: string
}

export function PavelChatSheet({ onClose, seedAssistantMessage, suggestedReplies, topicLabel }: Props) {
  // Telegram intercepts vertical swipes near the top of the viewport and minimises
  // the Mini App. The sheet has a handle visual but no drag-to-close, so the swipe
  // must not propagate to Telegram while it's open. (Bot API 7.7+)
  useEffect(() => {
    const wa = (window as unknown as { Telegram?: { WebApp?: any } }).Telegram?.WebApp
    if (!wa) return
    const wasEnabled = wa.isVerticalSwipesEnabled !== false
    try {
      wa.expand?.()
      wa.disableVerticalSwipes?.()
    } catch {
      // older Telegram clients — silently ignore
    }
    return () => {
      if (!wasEnabled) return
      try {
        wa.enableVerticalSwipes?.()
      } catch {
        // ignore
      }
    }
  }, [])

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 410 }}
      />

      {/* Sheet */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        style={{
          position: 'fixed',
          bottom: 0, left: 0, right: 0,
          zIndex: 411,
          height: '88vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(30px)',
          WebkitBackdropFilter: 'blur(30px)',
          borderRadius: '24px 24px 0 0',
          fontFamily: F,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 0', flexShrink: 0 }}>
          <SheetGrip onClose={onClose} />
        </div>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 11,
          padding: '10px 16px 12px',
          borderBottom: '1px solid rgba(26,58,92,0.07)',
          flexShrink: 0,
        }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'linear-gradient(135deg,#6C5CE7,#A29BFE)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(108,92,231,0.35)',
            }}>
              <Bot size={20} color="white" />
            </div>
            <span style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 10, height: 10, borderRadius: '50%',
              background: '#34C759', border: '2px solid white',
            }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#1C2D41' }}>ИИ Павел</span>
              <span style={{
                fontSize: 9,
                background: 'rgba(108,92,231,0.14)', color: '#6C5CE7',
                borderRadius: 5, padding: '1px 5px', fontWeight: 700,
              }}>AI</span>
            </div>
            <span style={{ fontSize: 12, color: topicLabel ? '#FF3B30' : '#6B7B8D', fontWeight: topicLabel ? 700 : 400 }}>
              {topicLabel ?? 'Персональный тренер · Онлайн'}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '50%',
              width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            <X size={16} color="#1C2D41" />
          </button>
        </div>

        {/* Chat body (reuses live backend via PavelChatBody) */}
        <PavelChatBody
          embedded
          seedAssistantMessage={seedAssistantMessage}
          suggestedReplies={suggestedReplies}
        />
      </motion.div>
    </>
  )
}
